import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { TierPracticeFilled } from '@heidi/design-system/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyH4,
  TypographyP2,
  typographyVariant,
} from '@heidi/design-system/typography';
import { Check, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';

import { RecommendedSeatBadge } from '../recommended-seat-badge/recommended-seat-badge';
import { practiceSeatUpgradeCardMessages as messages } from './practice-seat-upgrade-card.messages';

/** Which plan the team is coming from; it decides the CTA and feature list. */
export type PracticeSeatUpgradeCardFromPlan = 'practice' | 'scribeTeam';

const FROM_PLAN_COPY: Record<
  PracticeSeatUpgradeCardFromPlan,
  {
    cta: MessageDescriptor;
    title: MessageDescriptor;
    features: readonly MessageDescriptor[];
  }
> = {
  practice: {
    cta: messages.getPracticeMax,
    title: messages.everythingInPracticePlus,
    features: [
      messages.featureMaxUsage,
      messages.featureRegularAgenticUse,
      messages.featureEarlyAccess,
    ],
  },
  scribeTeam: {
    cta: messages.getPractice,
    title: messages.everythingInScribeTeamPlus,
    features: [
      messages.featureEvidenceAnswers,
      messages.featureEvidenceSources,
      messages.featureEvidenceLibraries,
    ],
  },
};

export type PracticeSeatUpgradeCardProps = {
  /** Formatted per-user monthly prices, e.g. "$130". */
  standardSeatPrice: string;
  maxSeatPrice: string;
  onGetPractice: () => void;
  isCtaDisabled?: boolean;
  isCtaLoading?: boolean;
  /** Explains a disabled CTA, e.g. "Contact your team admin to upgrade". */
  ctaDisabledTooltip?: string;
  /** The team's usage is high enough to recommend the Max seat. */
  isMaxSeatRecommended?: boolean;
  /**
   * `practice` (default): a Practice Standard team, so the list sells the Max
   * seat. `scribeTeam`: the list sells Practice over Scribe Team. Neither seat
   * is marked current: the checkout that follows lets the team pick either.
   */
  fromPlan?: PracticeSeatUpgradeCardFromPlan;
};

/** Practice plan card for a team on Practice Standard or Scribe Team, leading into the seat checkout. */
export function PracticeSeatUpgradeCard({
  standardSeatPrice,
  maxSeatPrice,
  onGetPractice,
  isCtaDisabled = false,
  isCtaLoading = false,
  ctaDisabledTooltip,
  isMaxSeatRecommended = false,
  fromPlan = 'practice',
}: PracticeSeatUpgradeCardProps) {
  const intl = useIntl();
  const copy = FROM_PLAN_COPY[fromPlan];
  const strong = (chunks: ReactNode[]) => (
    <span className={typographyVariant({ variant: 'p2Bold' })}>{chunks}</span>
  );

  const cta = (
    <Button
      variant="pro"
      size="md"
      className="w-full"
      onClick={onGetPractice}
      disabled={isCtaDisabled || isCtaLoading}
      aria-busy={isCtaLoading}
    >
      {isCtaLoading ? (
        <Loader2 className="animate-spin" aria-hidden />
      ) : (
        <TierPracticeFilled size={14} aria-hidden />
      )}
      {intl.formatMessage(copy.cta)}
    </Button>
  );

  return (
    <div className="flex w-full flex-col gap-6 rounded-xl border border-tier-pro-border bg-surface p-6">
      <div className="flex flex-col gap-4">
        <Badge size="sm" color="blue" className="self-start">
          {intl.formatMessage(messages.forCareTeams)}
        </Badge>
        <div className="flex items-center justify-between gap-4">
          <TypographyH4 className="text-text-primary">
            {intl.formatMessage(messages.practice)}
          </TypographyH4>
          <YearlyOnlyPill label={intl.formatMessage(messages.yearlyOnly)} />
        </div>
        <div className="flex flex-col gap-2">
          <SeatLine
            label={intl.formatMessage(messages.standardSeat, {
              price: standardSeatPrice,
              strong,
            })}
            description={intl.formatMessage(messages.standardSeatDescription)}
            badge={null}
          />
          <SeatLine
            label={intl.formatMessage(messages.maxSeat, { price: maxSeatPrice, strong })}
            description={intl.formatMessage(messages.maxSeatDescription)}
            badge={isMaxSeatRecommended ? <RecommendedSeatBadge /> : null}
          />
        </div>
      </div>
      <div className="flex flex-col items-center gap-2">
        {isCtaDisabled && ctaDisabledTooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block w-full">{cta}</span>
            </TooltipTrigger>
            <TooltipContent side="bottom">{ctaDisabledTooltip}</TooltipContent>
          </Tooltip>
        ) : (
          cta
        )}
        <TypographyCaptionBold className="text-center text-text-secondary">
          {intl.formatMessage(messages.selectSeatsNextStep)}
        </TypographyCaptionBold>
      </div>
      <div className="flex flex-col gap-2">
        <TypographyCaptionBold className="text-text-primary">
          {intl.formatMessage(copy.title)}
        </TypographyCaptionBold>
        <ul className="flex flex-col gap-2">
          {copy.features.map((feature) => (
            <li key={feature.id} className="flex items-start gap-2">
              <Check className="size-4 shrink-0 text-text-primary" aria-hidden />
              <TypographyCaption className="text-text-primary">
                {intl.formatMessage(feature)}
              </TypographyCaption>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * A one-option segmented control: a raised tab on the tertiary track, static
 * rather than a tablist. The track's radius is the tab's plus its 2px padding so
 * the rim stays even round the corners; the shadow is the segmented control's.
 */
function YearlyOnlyPill({ label }: { label: string }) {
  return (
    <span className="inline-flex shrink-0 rounded-[10px] bg-background-tertiary p-0.5">
      <span
        className={typographyVariant({
          variant: 'footnote',
          className:
            'rounded-md bg-surface px-2 py-1 leading-none text-text-primary [box-shadow:0_1px_3px_rgb(var(--shadow-color)/0.12),0_1px_1px_rgb(var(--shadow-color)/0.06)]',
        })}
      >
        {label}
      </span>
    </span>
  );
}

function SeatLine({
  label,
  description,
  badge,
}: {
  label: ReactNode;
  description: string;
  badge: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <TypographyP2 className="text-text-primary">{label}</TypographyP2>
        {badge}
      </div>
      <TypographyCaption className="text-text-secondary">{description}</TypographyCaption>
    </div>
  );
}
